package chand.wordvoyage

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeContentPadding
import androidx.compose.material3.Button
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.lifecycle.viewmodel.compose.viewModel
import chand.wordvoyage.platform.createAuthRepository
import chand.wordvoyage.platform.rememberGoogleSignInLauncher
import chand.wordvoyage.repository.AuthState
import chand.wordvoyage.viewmodel.AuthViewModel
import kotlinx.coroutines.delay

@Composable
fun LoginScreen(
    onLoginSuccess: (userId: String) -> Unit
) {
    val authRepository = remember { createAuthRepository() }
    val authViewModel: AuthViewModel = viewModel { AuthViewModel(authRepository) }
    val authState by authViewModel.authState.collectAsState()
    var signInError by remember { mutableStateOf<String?>(null) }
    var loadingTimedOut by remember { mutableStateOf(false) }

    LaunchedEffect(authState) {
        loadingTimedOut = false
        if (authState is AuthState.Loading) {
            delay(8000)
            loadingTimedOut = authState is AuthState.Loading
        }
    }

    // Navigate away if already authenticated
    if (authState is AuthState.Authenticated) {
        onLoginSuccess((authState as AuthState.Authenticated).userId)
        return
    }

    val googleSignInLauncher = rememberGoogleSignInLauncher(
        onSuccess = { idToken, _ ->
            signInError = null
            authViewModel.signInWithGoogle(idToken)
        },
        onFailure = { error ->
            signInError = error
            println("Google Sign-In failed: $error")
        }
    )

    Column(
        modifier = Modifier
            .background(MaterialTheme.colorScheme.primaryContainer)
            .safeContentPadding()
            .fillMaxSize()
            .padding(32.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.Center
    ) {
        Text(
            text = "WordVoyage",
            style = MaterialTheme.typography.displaySmall,
            color = MaterialTheme.colorScheme.onPrimaryContainer
        )

        Spacer(modifier = Modifier.height(8.dp))

        Text(
            text = "Discover & explore words",
            style = MaterialTheme.typography.bodyLarge,
            color = MaterialTheme.colorScheme.onPrimaryContainer.copy(alpha = 0.7f),
            textAlign = TextAlign.Center
        )

        Spacer(modifier = Modifier.height(64.dp))

        when (authState) {
            is AuthState.Loading -> {
                if (!loadingTimedOut) {
                    CircularProgressIndicator()
                    Spacer(modifier = Modifier.height(16.dp))
                    Text(
                        text = "Signing in...",
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onPrimaryContainer
                    )
                } else {
                    Text(
                        text = "The sign-in step is taking longer than expected. You can try again or restart the session.",
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onPrimaryContainer,
                        textAlign = TextAlign.Center,
                        modifier = Modifier.fillMaxWidth()
                    )
                    Spacer(modifier = Modifier.height(16.dp))
                    Button(
                        onClick = {
                            signInError = null
                            authViewModel.signOut()
                        },
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        Text("Restart sign-in")
                    }
                }
            }
            is AuthState.NotAuthenticated -> {
                signInError?.let { error ->
                    Text(
                        text = error,
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.error,
                        textAlign = TextAlign.Center,
                        modifier = Modifier.fillMaxWidth()
                    )
                    Spacer(modifier = Modifier.height(16.dp))
                }
                Button(
                    onClick = {
                        signInError = null
                        googleSignInLauncher()
                    },
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Text("Sign in with Google")
                }
            }
            else -> {}
        }
    }
}

